"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OsmosisSwapper = void 0;
const caip_1 = require("@shapeshiftoss/caip");
const api_1 = require("../../api");
const bignumber_1 = require("../utils/bignumber");
const constants_1 = require("./utils/constants");
const helpers_1 = require("./utils/helpers");
class OsmosisSwapper {
    constructor(deps) {
        this.name = api_1.SwapperName.Osmosis;
        this.deps = deps;
        this.supportedAssetIds = [caip_1.cosmosAssetId, caip_1.osmosisAssetId];
    }
    getType() {
        return api_1.SwapperType.Osmosis;
    }
    async getTradeTxs(tradeResult) {
        if (tradeResult.cosmosAddress) {
            const cosmosAdapter = this.deps.adapterManager.get(caip_1.cosmosChainId);
            if (!cosmosAdapter)
                throw new api_1.SwapError('OsmosisSwapper: couldnt get cosmos adapter', {
                    code: api_1.SwapErrorTypes.GET_TRADE_TXS_FAILED,
                });
            const cosmosTxHistory = await cosmosAdapter.getTxHistory({
                pubkey: tradeResult.cosmosAddress,
                pageSize: 1,
            });
            const currentCosmosTxid = cosmosTxHistory === null || cosmosTxHistory === void 0 ? void 0 : cosmosTxHistory.transactions[0].txid;
            return {
                sellTxid: tradeResult.tradeId,
                // This logic assumes there are the next cosmos tx will be the correct ibc transfer
                // a random incoming tx COULD cause this logic to fail but its unlikely
                // TODO find a better solution (may require unchained and parser additions)
                buyTxid: currentCosmosTxid !== tradeResult.previousCosmosTxid ? currentCosmosTxid : '',
            };
        }
        else {
            return {
                sellTxid: tradeResult.previousCosmosTxid,
                buyTxid: tradeResult.tradeId,
            };
        }
    }
    async getUsdRate(input) {
        const { symbol } = input;
        const sellAssetSymbol = symbol;
        const buyAssetSymbol = 'USDC';
        const sellAmount = '1';
        const { rate: osmoRate } = await (0, helpers_1.getRateInfo)('OSMO', buyAssetSymbol, sellAmount, this.deps.osmoUrl);
        if (sellAssetSymbol !== 'OSMO') {
            const { rate } = await (0, helpers_1.getRateInfo)(sellAssetSymbol, 'OSMO', sellAmount, this.deps.osmoUrl);
            return (0, bignumber_1.bnOrZero)(rate).times(osmoRate).toString();
        }
        return osmoRate;
    }
    async getMinMax(input) {
        const { sellAsset } = input;
        const usdRate = await this.getUsdRate({ ...sellAsset });
        const minimum = (0, bignumber_1.bn)(1).dividedBy((0, bignumber_1.bnOrZero)(usdRate)).toString();
        const maximum = constants_1.MAX_SWAPPER_SELL;
        return {
            minimum,
            maximum,
        };
    }
    async approvalNeeded() {
        return { approvalNeeded: false };
    }
    async approveInfinite() {
        throw new api_1.SwapError('OsmosisSwapper: approveInfinite unimplemented', {
            code: api_1.SwapErrorTypes.RESPONSE_ERROR,
        });
    }
    async approveAmount() {
        throw new api_1.SwapError('Osmosis: approveAmount unimplemented', {
            code: api_1.SwapErrorTypes.RESPONSE_ERROR,
        });
    }
    filterBuyAssetsBySellAssetId(args) {
        const { assetIds = [], sellAssetId } = args;
        if (!this.supportedAssetIds.includes(sellAssetId))
            return [];
        return assetIds.filter((assetId) => this.supportedAssetIds.includes(assetId) && assetId !== sellAssetId);
    }
    filterAssetIdsBySellable() {
        return this.supportedAssetIds;
    }
    async buildTrade(args) {
        const { sellAsset, buyAsset, sellAmountBeforeFeesCryptoBaseUnit: sellAmountCryptoBaseUnit, receiveAddress, bip44Params, } = args;
        if (!sellAmountCryptoBaseUnit) {
            throw new api_1.SwapError('sellAmountCryptoPrecision is required', {
                code: api_1.SwapErrorTypes.BUILD_TRADE_FAILED,
            });
        }
        const { buyAssetTradeFeeUsd, rate, buyAmountCryptoBaseUnit } = await (0, helpers_1.getRateInfo)(sellAsset.symbol, buyAsset.symbol, sellAmountCryptoBaseUnit !== '0' ? sellAmountCryptoBaseUnit : '1', this.deps.osmoUrl);
        //convert amount to base
        const sellAmountCryptoBase = String((0, bignumber_1.bnOrZero)(sellAmountCryptoBaseUnit).dp(0));
        const osmosisAdapter = this.deps.adapterManager.get(caip_1.osmosisChainId);
        if (!osmosisAdapter)
            throw new api_1.SwapError('Failed to get Osmosis adapter', {
                code: api_1.SwapErrorTypes.BUILD_TRADE_FAILED,
            });
        const feeData = await osmosisAdapter.getFeeData({});
        const fee = feeData.fast.txFee;
        return {
            buyAmountCryptoBaseUnit,
            buyAsset,
            feeData: {
                networkFeeCryptoBaseUnit: fee,
                sellAssetTradeFeeUsd: '0',
                buyAssetTradeFeeUsd,
            },
            rate,
            receiveAddress,
            sellAmountBeforeFeesCryptoBaseUnit: sellAmountCryptoBase,
            sellAsset,
            bip44Params,
            sources: [{ name: api_1.SwapperName.Osmosis, proportion: '100' }],
        };
    }
    async getTradeQuote(input) {
        const { bip44Params, sellAsset, buyAsset, sellAmountBeforeFeesCryptoBaseUnit: sellAmountCryptoBaseUnit, } = input;
        if (!sellAmountCryptoBaseUnit) {
            throw new api_1.SwapError('sellAmount is required', {
                code: api_1.SwapErrorTypes.RESPONSE_ERROR,
            });
        }
        const { buyAssetTradeFeeUsd, rate, buyAmountCryptoBaseUnit } = await (0, helpers_1.getRateInfo)(sellAsset.symbol, buyAsset.symbol, sellAmountCryptoBaseUnit !== '0' ? sellAmountCryptoBaseUnit : '1', this.deps.osmoUrl);
        const { minimum, maximum } = await this.getMinMax(input);
        const osmosisAdapter = this.deps.adapterManager.get(caip_1.osmosisChainId);
        if (!osmosisAdapter)
            throw new api_1.SwapError('Failed to get Osmosis adapter', {
                code: api_1.SwapErrorTypes.TRADE_QUOTE_FAILED,
            });
        const feeData = await osmosisAdapter.getFeeData({});
        const fee = feeData.fast.txFee;
        return {
            buyAsset,
            feeData: {
                networkFeeCryptoBaseUnit: fee,
                sellAssetTradeFeeUsd: '0',
                buyAssetTradeFeeUsd,
            },
            maximum,
            minimumCryptoHuman: minimum,
            bip44Params,
            rate,
            sellAsset,
            sellAmountBeforeFeesCryptoBaseUnit: sellAmountCryptoBaseUnit,
            buyAmountCryptoBaseUnit,
            sources: constants_1.DEFAULT_SOURCE,
            allowanceContract: '',
        };
    }
    async executeTrade({ trade, wallet }) {
        const { sellAsset, buyAsset, sellAmountBeforeFeesCryptoBaseUnit: sellAmountCryptoBaseUnit, bip44Params, receiveAddress, } = trade;
        const isFromOsmo = sellAsset.assetId === caip_1.osmosisAssetId;
        const sellAssetDenom = helpers_1.symbolDenomMapping[sellAsset.symbol];
        const buyAssetDenom = helpers_1.symbolDenomMapping[buyAsset.symbol];
        let ibcSellAmount;
        const osmosisAdapter = this.deps.adapterManager.get(caip_1.osmosisChainId);
        const cosmosAdapter = this.deps.adapterManager.get(caip_1.cosmosChainId);
        if (!cosmosAdapter || !osmosisAdapter) {
            throw new api_1.SwapError('Failed to get adapters', {
                code: api_1.SwapErrorTypes.EXECUTE_TRADE_FAILED,
            });
        }
        const feeData = await osmosisAdapter.getFeeData({});
        const gas = feeData.fast.chainSpecific.gasLimit;
        let sellAddress;
        let cosmosIbcTradeId = '';
        if (!isFromOsmo) {
            sellAddress = await cosmosAdapter.getAddress({ wallet, bip44Params });
            if (!sellAddress)
                throw new api_1.SwapError('Failed to get address', {
                    code: api_1.SwapErrorTypes.EXECUTE_TRADE_FAILED,
                });
            const transfer = {
                sender: sellAddress,
                receiver: receiveAddress,
                amount: sellAmountCryptoBaseUnit,
            };
            const responseAccount = await cosmosAdapter.getAccount(sellAddress);
            const accountNumber = responseAccount.chainSpecific.accountNumber || '0';
            const sequence = responseAccount.chainSpecific.sequence || '0';
            const { tradeId } = await (0, helpers_1.performIbcTransfer)(transfer, cosmosAdapter, wallet, this.deps.osmoUrl, 'uatom', constants_1.COSMO_OSMO_CHANNEL, feeData.fast.txFee, accountNumber, sequence, gas, 'uatom');
            cosmosIbcTradeId = tradeId;
            // wait till confirmed
            const pollResult = await (0, helpers_1.pollForComplete)(tradeId, this.deps.cosmosUrl);
            if (pollResult !== 'success')
                throw new api_1.SwapError('ibc transfer failed', {
                    code: api_1.SwapErrorTypes.EXECUTE_TRADE_FAILED,
                });
            ibcSellAmount = await (0, helpers_1.pollForAtomChannelBalance)(receiveAddress, this.deps.osmoUrl);
            // delay to ensure all nodes we interact with are up to date at this point
            // seeing intermittent bugs that suggest the balances and sequence numbers were sometimes off
            await new Promise((resolve) => setTimeout(resolve, 5000));
        }
        else {
            sellAddress = await osmosisAdapter.getAddress({ wallet, bip44Params });
            if (!sellAddress)
                throw new api_1.SwapError('failed to get osmoAddress', {
                    code: api_1.SwapErrorTypes.EXECUTE_TRADE_FAILED,
                });
        }
        const osmoAddress = isFromOsmo ? sellAddress : receiveAddress;
        const cosmosAddress = isFromOsmo ? receiveAddress : sellAddress;
        const signTxInput = await (0, helpers_1.buildTradeTx)({
            osmoAddress,
            adapter: osmosisAdapter,
            buyAssetDenom,
            sellAssetDenom,
            sellAmount: ibcSellAmount !== null && ibcSellAmount !== void 0 ? ibcSellAmount : sellAmountCryptoBaseUnit,
            gas,
            wallet,
        });
        const signed = await osmosisAdapter.signTransaction(signTxInput);
        const tradeId = await osmosisAdapter.broadcastTransaction(signed);
        if (isFromOsmo) {
            const pollResult = await (0, helpers_1.pollForComplete)(tradeId, this.deps.osmoUrl);
            if (pollResult !== 'success')
                throw new api_1.SwapError('osmo swap failed', {
                    code: api_1.SwapErrorTypes.EXECUTE_TRADE_FAILED,
                });
            const amount = await (0, helpers_1.pollForAtomChannelBalance)(sellAddress, this.deps.osmoUrl);
            const transfer = {
                sender: sellAddress,
                receiver: receiveAddress,
                amount,
            };
            const ibcResponseAccount = await osmosisAdapter.getAccount(sellAddress);
            const ibcAccountNumber = ibcResponseAccount.chainSpecific.accountNumber || '0';
            const ibcSequence = ibcResponseAccount.chainSpecific.sequence || '0';
            // delay to ensure all nodes we interact with are up to date at this point
            // seeing intermittent bugs that suggest the balances and sequence numbers were sometimes off
            await new Promise((resolve) => setTimeout(resolve, 5000));
            const cosmosTxHistory = await cosmosAdapter.getTxHistory({
                pubkey: cosmosAddress,
                pageSize: 1,
            });
            await (0, helpers_1.performIbcTransfer)(transfer, osmosisAdapter, wallet, this.deps.cosmosUrl, buyAssetDenom, constants_1.OSMO_COSMO_CHANNEL, '0', ibcAccountNumber, ibcSequence, gas, 'uosmo');
            return { tradeId, previousCosmosTxid: cosmosTxHistory.transactions[0].txid, cosmosAddress };
        }
        return { tradeId, previousCosmosTxid: cosmosIbcTradeId };
    }
}
exports.OsmosisSwapper = OsmosisSwapper;
